import {Transition as HeadlessTransition} from '@headlessui/react';
import cx from 'classnames';

import {TransitionClassNames} from '#constant/transition';

import {TransitionChildProps, TransitionProps} from './Transition.types';

/**
 * The Transition component lets you add enter/leave transitions to
 * conditionally rendered elements, using CSS classes to control the actual
 * transition styles in the different stages of the transition.
 *
 * It is build on top of the `Transition` component provided by "@headlessui",
 * and accepts the same prop interface. This version is pre-configured with
 * transition classnames that can be imported and used in your own styles from
 * '#constant/transition', which can be helpful when building many animated
 * components that make use of these classnames.
 *
 * For more info about `Transition`'s functionality (and its related
 * `Transition.Child` component), see: https://headlessui.com/react/transition
 */
export const Transition = <T extends React.ElementType = 'div'>({
  enter,
  enterFrom,
  enterTo,
  finish,
  leave,
  leaveTo,
  ...otherProps
}: TransitionProps<T>) => (
  <HeadlessTransition
    enter={cx(enter, TransitionClassNames.transition, TransitionClassNames.enter)}
    enterFrom={cx(enterFrom, TransitionClassNames.start)}
    enterTo={cx(enterTo, TransitionClassNames.finish)}
    leave={cx(leave, TransitionClassNames.transition, TransitionClassNames.leave)}
    leaveFrom={cx(finish, TransitionClassNames.finish)}
    leaveTo={cx(leaveTo, TransitionClassNames.start)}
    {...otherProps}
  />
);

export const Child = <T extends React.ElementType = 'div'>({
  enter,
  enterFrom,
  enterTo,
  finish,
  leave,
  leaveTo,
  ...otherProps
}: TransitionChildProps<T>) => (
  <HeadlessTransition.Child
    enter={cx(enter, TransitionClassNames.transition, TransitionClassNames.enter)}
    enterFrom={cx(enterFrom, TransitionClassNames.start)}
    enterTo={cx(enterTo, TransitionClassNames.finish)}
    leave={cx(leave, TransitionClassNames.transition, TransitionClassNames.leave)}
    leaveFrom={cx(finish, TransitionClassNames.finish)}
    leaveTo={cx(leaveTo, TransitionClassNames.start)}
    {...otherProps}
  />
);

Transition.Child = Child;
